// packages/api/src/PublicContactService.ts
//
// PUBLIC CONTACT US client — the read (`publicContactContext`) that returns the
// recognized-sender prefill + the audience gate, and the write (`publicContactMessage`)
// that submits a message. Anonymous + published-only, mirroring eventsmagic-api
// PublicRsvpCore.contactMessage / contactContext.
//
// Runs through the injected client (see `configureApiService`) so it works in the
// BROWSER — the contact form is an interactive client component.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

export interface PublicContactMessageInput {
  url: string;
  visitorId?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  subject?: string | null;
  message: string;
  /** Honeypot — leave empty; a filled value is silently dropped by the server. */
  website?: string | null;
}

export interface PublicContactContext {
  /** Whether the event has a usable Contact Us block at all. */
  enabled?: boolean | null;
  /** Whether anonymous visitors may send (else recognized guests only). */
  allowAnonymous?: boolean | null;
  /** Whether this browser's visitorId resolves to a recognized guest. */
  recognized?: boolean | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
}

const PUBLIC_CONTACT_CONTEXT_QUERY = gql`
  query PublicContactContext($url: String!, $visitorId: String) {
    publicContactContext(url: $url, visitorId: $visitorId) {
      code
      errors {
        name
        errors
      }
      data {
        enabled
        allowAnonymous
        recognized
        firstName
        lastName
        email
        phone
      }
    }
  }
`;

const PUBLIC_CONTACT_MESSAGE_MUTATION = gql`
  mutation PublicContactMessage($params: PublicContactMessageInput) {
    publicContactMessage(params: $params) {
      code
      errors {
        name
        errors
      }
      data {
        ok
      }
    }
  }
`;

export class PublicContactService {
  /**
   * Load the recognized-sender prefill + audience gate for the Contact Us block. A
   * recognized visitor gets their own name/email/phone back so the form can prefill +
   * disable those fields. Always a fresh network read.
   */
  static async getContext(
    args: { url: string; visitorId?: string | null },
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return ApiService.executeQuery(
      PUBLIC_CONTACT_CONTEXT_QUERY,
      { url: args.url, visitorId: args.visitorId ?? null },
      { fetchPolicy: "network-only", ...options }
    );
  }

  /**
   * Send a Contact Us message from the event website. Anonymous, rate-limited, and
   * audience-gated on the server. Pass `visitorId` so a recognized guest is attributed.
   */
  static async contactMessage(
    input: PublicContactMessageInput,
    options: Record<string, any> = {}
  ): Promise<OpResult> {
    return ApiService.executeMutation(PUBLIC_CONTACT_MESSAGE_MUTATION, { params: input }, options);
  }
}
